/**
 * usePendingToast Hook
 *
 * Checks for pending toast messages stored in sessionStorage after navigation
 * or when returning to the tab. This is needed because toasts triggered during
 * navigation or tab switches may not display if the Toaster component re-renders.
 */

import React, { useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import { PENDING_TOAST_KEY } from '../utils/toast';
import { Mic } from '../components/icons';

// Dynamically import toast to avoid circular dependencies
let toast: any = null;
if (Platform.OS === 'web') {
  try {
    toast = require('react-hot-toast').default;
  } catch {
    // Toast not available
  }
}

/**
 * Hook that checks for and displays pending toast messages after route changes
 * or when returning to the tab from being hidden
 * @param currentRoute - The current route path, triggers re-check on change
 */
export function usePendingToast(currentRoute: string) {
  // Shared function to check and display pending toast
  const checkAndShowPendingToast = useCallback(() => {
    if (Platform.OS !== 'web' || !toast) return;

    const pending = sessionStorage.getItem(PENDING_TOAST_KEY);
    if (pending) {
      sessionStorage.removeItem(PENDING_TOAST_KEY);
      try {
        const { type, message } = JSON.parse(pending);
        if (type === 'info') {
          toast(message, {
            duration: 3000,
            style: {
              cursor: 'pointer',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              border: '1px solid #f59e0b',
            },
            icon: React.createElement(Mic, { size: 18, color: '#92400e' }),
          });
        } else if (type === 'success') {
          toast.success(message, { duration: 3000 });
        } else if (type === 'error') {
          toast.error(message, { duration: 3000 });
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Check for pending toast on route change
  useEffect(() => {
    if (Platform.OS !== 'web' || !toast) return;

    // Delay to ensure Toaster component is mounted and stable
    const timeoutId = setTimeout(checkAndShowPendingToast, 200);

    return () => clearTimeout(timeoutId);
  }, [currentRoute, checkAndShowPendingToast]);

  // Check for pending toast when tab becomes visible again
  useEffect(() => {
    if (Platform.OS !== 'web' || !toast) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to ensure UI is ready
        setTimeout(checkAndShowPendingToast, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndShowPendingToast]);
}
